// The wa-archive v1 formats, in the browser.
//
// A direct port of contracts/reference/format.mjs onto WebCrypto and
// Uint8Array. It must agree with the Rust core byte for byte — a pack written
// on a Pixel has to open here — so every value is checked against
// contracts/vectors.json by test/format.test.mjs.
//
// This is temporary in one sense only: A3's Rust core compiled to wasm
// replaces it. The formats it implements do not change.

export const MAGIC = new TextEncoder().encode('WAP1');
export const VERSION = 1;
export const DEFAULT_FRAME_SIZE = 1 << 20;
export const ENTRY_SIZE = 64;
export const FOOTER_SIZE = 24;
export const TAG_SIZE = 16;
export const NONCE_PREFIX_SIZE = 4;

export const KIND = {
  MESSAGE_CHUNK: 0, IMAGE: 1, VIDEO: 2, AUDIO: 3,
  DOCUMENT: 4, THUMBNAIL: 5, MANIFEST: 6, CHUNK_DIRECTORY: 7, SEARCH_INDEX: 8,
} as const;
export type Kind = (typeof KIND)[keyof typeof KIND];

const enc = new TextEncoder();
const subtle = () => globalThis.crypto.subtle;

export const toHex = (b: Uint8Array) =>
  Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
export const fromHex = (h: string) =>
  new Uint8Array((h.match(/../g) ?? []).map((x) => parseInt(x, 16)));

function concat(parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let o = 0;
  for (const p of parts) { out.set(p, o); o += p.length; }
  return out;
}
export const equal = (a: Uint8Array, b: Uint8Array) =>
  a.length === b.length && a.every((x, i) => x === b[i]);

// ----------------------------------------------------------------- keys

async function hkdf(ikm: Uint8Array, salt: Uint8Array, info: Uint8Array): Promise<Uint8Array> {
  const key = await subtle().importKey('raw', ikm as BufferSource, 'HKDF', false, ['deriveBits']);
  const bits = await subtle().deriveBits(
    { name: 'HKDF', hash: 'SHA-256', salt: salt as BufferSource, info: info as BufferSource }, key, 256);
  return new Uint8Array(bits);
}

export function kek(secret: Uint8Array, salt: Uint8Array, method: 'passkey' | 'recovery') {
  return hkdf(secret, salt, enc.encode(`wa-archive/v1/kek/${method}`));
}

async function aesKey(raw: Uint8Array, usages: KeyUsage[]) {
  return subtle().importKey('raw', raw as BufferSource, 'AES-GCM', false, usages);
}

/** 12-byte nonce || 32-byte ciphertext || 16-byte tag = 60 bytes. */
export async function wrapAccountKey(accountKey: Uint8Array, kekBytes: Uint8Array, nonce: Uint8Array) {
  if (accountKey.length !== 32) throw new Error('account key must be 32 bytes');
  if (nonce.length !== 12) throw new Error('wrap nonce must be 12 bytes');
  const ct = await subtle().encrypt(
    { name: 'AES-GCM', iv: nonce as BufferSource, tagLength: 128 },
    await aesKey(kekBytes, ['encrypt']), accountKey as BufferSource);
  return concat([nonce, new Uint8Array(ct)]);
}

export async function unwrapAccountKey(wrapped: Uint8Array, kekBytes: Uint8Array) {
  if (wrapped.length !== 60) throw new Error('wrapped key must be 60 bytes');
  const pt = await subtle().decrypt(
    { name: 'AES-GCM', iv: wrapped.subarray(0, 12) as BufferSource, tagLength: 128 },
    await aesKey(kekBytes, ['decrypt']), wrapped.subarray(12) as BufferSource);
  return new Uint8Array(pt);
}

/** Keyed, so the server can dedupe within an account but cannot probe it. */
export async function contentId(accountKey: Uint8Array, plaintext: Uint8Array) {
  const key = await subtle().importKey(
    'raw', accountKey as BufferSource, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  return new Uint8Array(await subtle().sign('HMAC', key, plaintext as BufferSource));
}

/**
 * Chat titles.
 *
 * The column has always been called `title_ciphertext` and has always held
 * `btoa(title)`, which is base64 — the server could read the name of every
 * conversation, while the privacy policy said it could not. This is the fix.
 *
 * Its own derived key rather than the account key directly, so a title and a
 * member never share one; and nonce-prefixed output, so the same name in two
 * accounts, or renamed back to itself, does not produce the same bytes.
 */
const labelKey = (accountKey: Uint8Array) =>
  hkdf(accountKey, new Uint8Array(32), enc.encode('wa-archive/v1/label'));

export async function sealBytes(accountKey: Uint8Array, plain: Uint8Array): Promise<string> {
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const key = await aesKey(await labelKey(accountKey), ['encrypt']);
  const ct = new Uint8Array(await subtle().encrypt(
    { name: 'AES-GCM', iv: nonce as BufferSource }, key, plain as BufferSource));
  const out = new Uint8Array(nonce.length + ct.length);
  out.set(nonce); out.set(ct, nonce.length);
  // Chunked: String.fromCharCode(...bytes) overflows the argument list on an
  // avatar, which is the first thing here big enough to reach that limit.
  let s = '';
  for (let i = 0; i < out.length; i += 0x8000)
    s += String.fromCharCode(...out.subarray(i, i + 0x8000));
  return btoa(s);
}

export async function openBytes(accountKey: Uint8Array, b64: string): Promise<Uint8Array | null> {
  try {
    const raw = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    if (raw.length < 12 + TAG_SIZE) return null;
    const key = await aesKey(await labelKey(accountKey), ['decrypt']);
    return new Uint8Array(await subtle().decrypt(
      { name: 'AES-GCM', iv: raw.subarray(0, 12) as BufferSource }, key,
      raw.subarray(12) as BufferSource));
  } catch {
    return null;
  }
}

export const sealLabel = (accountKey: Uint8Array, text: string) =>
  sealBytes(accountKey, enc.encode(text));

/**
 * Null when the bytes are not ours to open — which is how a row written before
 * this existed is recognised. GCM's tag makes a plaintext title decrypting by
 * accident not worth defending against.
 */
export async function openLabel(accountKey: Uint8Array, b64: string): Promise<string | null> {
  const raw = await openBytes(accountKey, b64);
  return raw === null ? null : new TextDecoder().decode(raw);
}

/**
 * A title written before titles were sealed.
 *
 * The old scheme was `btoa(title)`, which writes one byte per UTF-16 code
 * unit — Latin-1, not UTF-8. Reading those bytes back as UTF-8 turns every
 * accented character into U+FFFD: "Gl\u00eanio" became "Gl\uFFFDnio", because
 * \u00ea is U+00EA and a lone 0xEA is not a valid UTF-8 sequence.
 *
 * UTF-8 first and strictly, because a later variant of the old code may have
 * encoded properly and a plain ASCII title is valid in both. Latin-1 only
 * when UTF-8 refuses, which is exactly the case that was being mangled.
 *
 * @returns null when the input is not base64 at all.
 */
export function openLegacyLabel(b64: string): string | null {
  let bytes: Uint8Array;
  try { bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0)); }
  catch { return null; }
  try { return new TextDecoder('utf-8', { fatal: true }).decode(bytes); }
  catch { return new TextDecoder('latin1').decode(bytes); }
}

/** A title that came back with replacement characters is not a title. */
export const isMangled = (s: string) => s.includes('\uFFFD');

export function memberKey(accountKey: Uint8Array, cid: Uint8Array) {
  return hkdf(accountKey, cid, enc.encode('wa-archive/v1/member'));
}

// -------------------------------------------------------------- framing

function nonceFor(prefix: Uint8Array, i: number) {
  const n = new Uint8Array(12);
  n.set(prefix, 0);
  new DataView(n.buffer).setBigUint64(NONCE_PREFIX_SIZE, BigInt(i), false);
  return n;
}

/** Binds each frame to its member, its index and its length. The length is
 *  what stops a truncation attack on the final frame. */
function aadFor(cid: Uint8Array, i: number, plainLen: number) {
  const a = new Uint8Array(44);
  a.set(cid, 0);
  const dv = new DataView(a.buffer);
  dv.setBigUint64(32, BigInt(i), false);
  dv.setUint32(40, plainLen, false);
  return a;
}

export const frameCount = (plainLength: number, frameSize: number) =>
  Math.ceil(plainLength / frameSize);

/** Arithmetic, not a stored table: every frame but the last is exactly
 *  frameSize plaintext bytes. */
export function frameExtent(i: number, plainLength: number, frameSize: number) {
  const plain = Math.min(frameSize, plainLength - i * frameSize);
  return { offset: NONCE_PREFIX_SIZE + i * (frameSize + TAG_SIZE), length: plain + TAG_SIZE, plain };
}

export interface EncryptedMember {
  contentId: Uint8Array;
  payload: Uint8Array;
  plainLength: number;
  frameSize: number;
}

export async function encryptMember(
  accountKey: Uint8Array, plaintext: Uint8Array,
  noncePrefix: Uint8Array, frameSize = DEFAULT_FRAME_SIZE,
): Promise<EncryptedMember> {
  if (noncePrefix.length !== NONCE_PREFIX_SIZE) throw new Error('nonce prefix must be 4 bytes');
  const cid = await contentId(accountKey, plaintext);
  const key = await aesKey(await memberKey(accountKey, cid), ['encrypt']);
  const parts: Uint8Array[] = [noncePrefix];
  for (let i = 0; i < frameCount(plaintext.length, frameSize); i++) {
    const pt = plaintext.subarray(i * frameSize, Math.min((i + 1) * frameSize, plaintext.length));
    const ct = await subtle().encrypt(
      { name: 'AES-GCM', iv: nonceFor(noncePrefix, i) as BufferSource,
        additionalData: aadFor(cid, i, pt.length) as BufferSource, tagLength: 128 },
      key, pt as BufferSource);
    parts.push(new Uint8Array(ct));
  }
  return { contentId: cid, payload: concat(parts), plainLength: plaintext.length, frameSize };
}

/** Decrypt only the frames covering [from, to]. `read` fetches bytes from the
 *  member payload — a ranged GET through the Service Worker in the app. */
export async function decryptRange(
  accountKey: Uint8Array, cid: Uint8Array, plainLength: number, frameSize: number,
  read: (offset: number, length: number) => Promise<Uint8Array> | Uint8Array,
  from: number, to: number,
): Promise<Uint8Array> {
  if (from < 0 || to >= plainLength || from > to) throw new Error('range out of bounds');
  const key = await aesKey(await memberKey(accountKey, cid), ['decrypt']);
  const prefix = await read(0, NONCE_PREFIX_SIZE);
  const first = Math.floor(from / frameSize), last = Math.floor(to / frameSize);
  const out: Uint8Array[] = [];
  for (let i = first; i <= last; i++) {
    const { offset, length, plain } = frameExtent(i, plainLength, frameSize);
    const buf = await read(offset, length);
    const pt = await subtle().decrypt(
      { name: 'AES-GCM', iv: nonceFor(prefix, i) as BufferSource,
        additionalData: aadFor(cid, i, plain) as BufferSource, tagLength: 128 },
      key, buf as BufferSource);
    out.push(new Uint8Array(pt));
  }
  const joined = concat(out);
  const start = from - first * frameSize;
  return joined.subarray(start, start + (to - from + 1));
}

// ----------------------------------------------------------------- pack

export interface PackMember extends EncryptedMember { kind: Kind }
export interface IndexEntry {
  contentId: Uint8Array; payloadOffset: number; payloadLength: number;
  plainLength: number; frameSize: number; kind: Kind;
}

export async function writePack(members: PackMember[]): Promise<Uint8Array> {
  const head = new Uint8Array(8);
  head.set(MAGIC, 0);
  head[4] = VERSION;

  const body: Uint8Array[] = [];
  const entries: Uint8Array[] = [];
  let off = head.length;
  for (const m of members) {
    const e = new Uint8Array(ENTRY_SIZE);
    const dv = new DataView(e.buffer);
    e.set(m.contentId, 0);
    dv.setBigUint64(32, BigInt(off), true);
    dv.setBigUint64(40, BigInt(m.payload.length), true);
    dv.setBigUint64(48, BigInt(m.plainLength), true);
    dv.setUint32(56, m.frameSize, true);
    e[60] = m.kind;
    entries.push(e);
    body.push(m.payload);
    off += m.payload.length;
  }

  const count = new Uint8Array(4);
  new DataView(count.buffer).setUint32(0, entries.length, true);
  const index = concat([count, ...entries]);

  // Index at the tail, so a writer streams without knowing offsets in advance
  // and a reader gets it with `Range: bytes=-24` plus one more ranged GET.
  const footer = new Uint8Array(FOOTER_SIZE);
  const fdv = new DataView(footer.buffer);
  fdv.setBigUint64(0, BigInt(off), true);
  fdv.setBigUint64(8, BigInt(index.length), true);
  const digest = new Uint8Array(await subtle().digest('SHA-256', index as BufferSource));
  footer.set(digest.subarray(0, 4), 16);
  footer.set(MAGIC, 20);

  return concat([head, ...body, index, footer]);
}

/** A pack sealed as a Blob.
 *
 *  `writePack` returns one contiguous Uint8Array, which is fine for the test
 *  vectors and fatal for a real archive: a 3.5 GB export needs a single
 *  ArrayBuffer larger than V8 will allocate, and the import dies with
 *  "Array buffer allocation failed". A Blob is a list of parts the browser can
 *  back on disk, and fetch streams it without ever making it contiguous.
 */
export interface SealedPack { blob: Blob; index: IndexEntry[]; size: number }

export async function sealPack(members: PackMember[]): Promise<SealedPack> {
  const head = new Uint8Array(8);
  head.set(MAGIC, 0);
  head[4] = VERSION;

  const parts: BlobPart[] = [head as BlobPart];
  const entries: Uint8Array[] = [];
  const index: IndexEntry[] = [];
  let off = head.length;

  for (const m of members) {
    const e = new Uint8Array(ENTRY_SIZE);
    const dv = new DataView(e.buffer);
    e.set(m.contentId, 0);
    dv.setBigUint64(32, BigInt(off), true);
    dv.setBigUint64(40, BigInt(m.payload.length), true);
    dv.setBigUint64(48, BigInt(m.plainLength), true);
    dv.setUint32(56, m.frameSize, true);
    e[60] = m.kind;
    entries.push(e);
    index.push({
      contentId: m.contentId, payloadOffset: off, payloadLength: m.payload.length,
      plainLength: m.plainLength, frameSize: m.frameSize, kind: m.kind,
    });
    parts.push(m.payload as BlobPart);
    off += m.payload.length;
  }

  const count = new Uint8Array(4);
  new DataView(count.buffer).setUint32(0, entries.length, true);
  const indexBytes = concat([count, ...entries]);

  const footer = new Uint8Array(FOOTER_SIZE);
  const fdv = new DataView(footer.buffer);
  fdv.setBigUint64(0, BigInt(off), true);
  fdv.setBigUint64(8, BigInt(indexBytes.length), true);
  const digest = new Uint8Array(await subtle().digest('SHA-256', indexBytes as BufferSource));
  footer.set(digest.subarray(0, 4), 16);
  footer.set(MAGIC, 20);

  parts.push(indexBytes as BlobPart, footer as BlobPart);
  return { blob: new Blob(parts), index, size: off + indexBytes.length + FOOTER_SIZE };
}

export function readFooter(last24: Uint8Array) {
  if (last24.length !== FOOTER_SIZE) throw new Error('footer must be 24 bytes');
  if (!equal(last24.subarray(20), MAGIC)) throw new Error('not a pack: bad trailing magic');
  const dv = new DataView(last24.buffer, last24.byteOffset, last24.byteLength);
  return { indexOffset: Number(dv.getBigUint64(0, true)),
           indexLength: Number(dv.getBigUint64(8, true)),
           indexDigest: last24.subarray(16, 20) };
}

export async function readIndex(indexBytes: Uint8Array, expectDigest?: Uint8Array): Promise<IndexEntry[]> {
  if (expectDigest) {
    const d = new Uint8Array(await subtle().digest('SHA-256', indexBytes as BufferSource)).subarray(0, 4);
    if (!equal(d, expectDigest)) throw new Error('index digest mismatch');
  }
  const dv = new DataView(indexBytes.buffer, indexBytes.byteOffset, indexBytes.byteLength);
  const n = dv.getUint32(0, true);
  const out: IndexEntry[] = [];
  for (let i = 0; i < n; i++) {
    const o = 4 + i * ENTRY_SIZE;
    out.push({
      contentId: indexBytes.slice(o, o + 32),
      payloadOffset: Number(dv.getBigUint64(o + 32, true)),
      payloadLength: Number(dv.getBigUint64(o + 40, true)),
      plainLength: Number(dv.getBigUint64(o + 48, true)),
      frameSize: dv.getUint32(o + 56, true),
      kind: indexBytes[o + 60] as Kind,
    });
  }
  return out;
}

// ------------------------------------------------------- canonical JSON

/** Deterministic, because the content id is an HMAC over these bytes. */
export function canonicalJSON(value: unknown): Uint8Array {
  return enc.encode(encodeValue(value));
}

function encodeValue(v: unknown): string {
  if (v === null) throw new Error('null is not representable — omit the field');
  if (typeof v === 'string') return JSON.stringify(v.normalize('NFC'));
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'number') {
    if (!Number.isInteger(v)) throw new Error(`non-integer number: ${v}`);
    return String(v);
  }
  if (typeof v === 'bigint') return String(v);
  if (Array.isArray(v)) return '[' + v.map(encodeValue).join(',') + ']';
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    const keys = Object.keys(o).filter((k) => o[k] !== undefined).sort();
    return '{' + keys.map((k) => JSON.stringify(k.normalize('NFC')) + ':' + encodeValue(o[k])).join(',') + '}';
  }
  throw new Error(`not representable: ${typeof v}`);
}
